import type { MessageDataObject } from "@/composables/useIframeConnection";
import { useIframeConnection } from "@/composables/useIframeConnection";
import {
  createModalCloseMessage,
  IFRAME_MESSAGE_TYPES,
  iframeBridge,
  type AnotherClientCreatedPayload,
  type ClientDataEditedPayload,
  type DealCreatedPayload,
  type EventArgs,
  type EventArgsOf,
  type IframeBridgeEvent,
  type IframeModalType,
  type ModalClosePayloadByType,
  type NewClientModalClosePayload,
  type PayloadOf,
  type ResumeAfterClientCreatedPayload,
} from "@/composables/iframeBridgeProtocol";
import { usePreloadedIframe } from "@/composables/usePreloadedIframe";

export const useIframeBridge = () => {
  const { send } = useIframeConnection();

  // Dispatches CLOSE_MODAL on the parent (same) window so iframeModal.vue listeners
  // can close the matching dialog. `send()` would target the iframe contentWindow instead.
  const postParentModalCloseMessage = (message: MessageDataObject) => {
    window.postMessage(message, window.location.origin);
  };

  const closeByModalType = <M extends IframeModalType>(
    modalType: M,
    ...args: EventArgs<ModalClosePayloadByType[M]>
  ) => {
    const { isVisible, currentModalName, closeModal } = usePreloadedIframe();
    // The parent modal can live in the preloaded iframe, whose host rejects
    // same-window postMessage (source check, usePreloadedIframe.ts:177). Close it
    // there directly; otherwise fall back to the legacy iframeModal.vue path.
    if (isVisible.value && currentModalName.value === modalType) {
      closeModal(args[0]);
      return;
    }
    postParentModalCloseMessage(
      createModalCloseMessage(modalType, ...args)
    );
  };

  /** Modal close is the exception that targets the parent window, not the iframe. */
  const modalClose = {
    closeByModalType,
    closeNewDealModal: (
      payload?: ModalClosePayloadByType[typeof IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL]
    ) => {
      closeByModalType(IFRAME_MESSAGE_TYPES.NEW_DEAL_MODAL, payload);
    },
  };

  /**
   * Regular bridge emit path on the parent side.
   * `useIframeConnection().send()` posts from fincrm into the lendi iframe.
   */
  const emit = <E extends IframeBridgeEvent<any>>(
    event: E,
    ...args: EventArgsOf<E>
  ) => {
    const create = event.create as (
      payload?: PayloadOf<E>
    ) => ReturnType<E["create"]>;
    send(create(args[0] as PayloadOf<E> | undefined));
  };

  /** Parent -> iframe bridge helpers. `send()` posts into `iframe.contentWindow`. */
  const toLendiCrm = {
    newDeal: {
      selectExistingClient: (payload: NewClientModalClosePayload) => {
        emit(iframeBridge.toLendiCrm.newDeal.existingClientSelected, payload);
      },
    },
    newDealWithClientCreation: {
      resumeAfterAdditionalClientCreated: (
        payload: AnotherClientCreatedPayload
      ) => {
        emit(
          iframeBridge.toLendiCrm.newDealWithClientCreation
            .anotherClientCreated,
          payload
        );
      },
      resumeAfterClientCreated: (
        payload: ResumeAfterClientCreatedPayload
      ) => {
        emit(
          iframeBridge.toLendiCrm.newDealWithClientCreation
            .resumeAfterClientCreated,
          payload
        );
      },
    },
    cart: {
      refreshAfterDealCreated: (payload: DealCreatedPayload) => {
        emit(iframeBridge.toLendiCrm.cart.dealCreated, payload);
      },
      applyEditedClientData: (payload: ClientDataEditedPayload) => {
        emit(iframeBridge.toLendiCrm.cart.clientDataEdited, payload);
      },
    },
  };

  return {
    toLendiCrm,
    modalClose,
    fromLendiCrm: iframeBridge.toFincrm,
    infra: iframeBridge.infra,
    protocol: iframeBridge,
  };
};
